import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles.filter((a) => a.id !== id).slice(0, 3);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 黄瓜视频在线`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20 px-4">
          <div className="max-w-[800px] mx-auto text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `https://www.huangguaTV.com/article/${article.id}#article`,
        headline: article.title,
        description: article.summary,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.date,
        dateModified: article.date,
        publisher: {
          '@type': 'Organization',
          name: '黄瓜视频在线',
          url: 'https://www.huangguaTV.com',
        },
        inLanguage: 'zh-CN',
        isPartOf: { '@id': 'https://www.huangguaTV.com/#website' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://www.huangguaTV.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '资讯',
            item: 'https://www.huangguaTV.com/category/zixun',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
          },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />

      <main className="pt-20 bg-background-50">
        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[800px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '资讯', path: '/category/zixun' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 pb-14">
          <div className="max-w-[800px] mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-2.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {article.date}
                </span>
              </div>
            </div>

            <div className="relative w-full aspect-video md:h-[400px] rounded-xl overflow-hidden mb-8">
              <img
                src={article.thumbnail}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-14 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={related.thumbnail}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-5">
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700 mb-2">
                        {related.category}
                      </span>
                      <h3 className="font-heading text-base text-foreground-900 mb-2 line-clamp-2">
                        {related.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2">
                        {related.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}